import { useCallback, useEffect, useMemo } from 'react';
import { useViewportUtils } from './useViewportUtils';
import { useViewportInitialization } from './useViewportInitialization';
import { useFlowchartKeyboard } from './useFlowchartKeyboard';
import { useClipboard } from './useClipboard';
import { useHoveredNode } from './useHoveredNode';
import { useCampaignDrop } from './useCampaignDrop';
import { useFlowchartCoreNodeActions } from './useFlowchartCoreNodeActions';
import { useCanvasContextMenu } from './useCanvasContextMenu';
import { useViewportVirtualization } from './useViewportVirtualization';

/**
 * Viewport virtualization, placement helpers, clipboard/context-menu, and window listeners after focus mode is resolved.
 */
export function useFlowchartCoreAuxiliaryPipeline({
  immediateNestAction,
  onNestTasks,
  clearImmediateNestAction,
  focusCore,
  interaction,
  isAnimatingLayout,
  nodeDimensions,
  tasks,
  nodePositions,
  customPositionsRef,
  campaignId,
  autoPositions,
  setPanUnconstrained,
  setZoom,
  handleGoToStart,
  skipResizeAdjustment,
  onCreateConnectedTask,
  onInsertNodeOnEdge,
  onInsertMilestoneOnEdge,
  handleAutoLayout,
  followTaskAfterLayout,
  nestedSprints,
  allTags,
  customFields,
  onPasteTasks,
  onDeleteTasks,
  handleAfterDeleteStable,
  isMultiCampaignMode,
  openCampaignPickerForPaste,
  loadFullSprint,
  allSprints,
  onAddTask,
  setSelectedNodes,
  setPanAnimated,
  onNestCampaign,
  onAddCampaignToView,
  multiCampaignCampaigns,
  setCanvasContextMenuState,
  canvasMenuRef,
}) {
  const {
    tasksToRender,
    positionsToUse,
    edgesToRender: focusEdgesToRender,
    isFocusMode,
    focusedTaskIds,
    allFocusedTaskIds,
    addToFocusedTasks,
    centerOnTask,
    focusSelectedTasks,
    handleExitFocusMode,
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus,
  } = focusCore;

  const {
    containerRef,
    pan,
    zoom,
    selectedNodes,
    draggingNode,
    connectingFrom,
  } = interaction;

  useEffect(() => {
    if (immediateNestAction && onNestTasks) {
      onNestTasks(immediateNestAction);
      clearImmediateNestAction();
    }
  }, [immediateNestAction, onNestTasks, clearImmediateNestAction]);

  const hoveredNodeId = useHoveredNode(containerRef);

  const virtualization = useViewportVirtualization({
    tasks: tasksToRender,
    positionsToUse,
    nodeDimensions,
    containerRef,
    pan,
    zoom,
    selectedNodes,
    draggingNode,
    connectingFrom,
    hoveredNodeId,
    isAnimatingLayout,
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus,
  });

  const {
    visibleTasks: virtualizedTasksToRender,
    fullNodeIds,
    filterVisibleEdges,
  } = virtualization;

  const virtualizedEdgesToRender = useMemo(
    () => filterVisibleEdges(focusEdgesToRender),
    [filterVisibleEdges, focusEdgesToRender, pan.x, pan.y, zoom],
  );

  const viewportUtils = useViewportUtils({
    containerRef,
    pan,
    zoom,
    tasks,
    nodePositions,
    nodeDimensions,
    customPositionsRef,
  });

  const {
    getViewportCenter,
    findEmptySpaceInViewport: findEmptySpaceInViewportFromUtils,
    findEmptySpaceNearNode: findEmptySpaceNearNodeFromUtils,
    findEmptySpaceNearPoint: findEmptySpaceNearPointFromUtils,
    findPositionInCampaign,
  } = viewportUtils;

  const { isViewReady } = useViewportInitialization({
    campaignId,
    tasks,
    autoPositions,
    nodeDimensions,
    containerRef,
    pan,
    zoom,
    setPan: setPanUnconstrained,
    setZoom,
    handleGoToStart: () => handleGoToStart(setZoom),
    skipResizeAdjustment,
    isFocusMode,
  });

  const nodeActions = useFlowchartCoreNodeActions({
    tasks,
    interaction,
    findEmptySpaceNearNode: findEmptySpaceNearNodeFromUtils,
    findEmptySpaceNearPoint: findEmptySpaceNearPointFromUtils,
    onCreateConnectedTask,
    onInsertNodeOnEdge,
    onInsertMilestoneOnEdge,
    handleAutoLayout,
    followTaskAfterLayout,
    isFocusMode,
    focusedTaskIds,
    allFocusedTaskIds,
    addToFocusedTasks,
  });

  const { setTaskPosition, handleCreateConnectedNode, handleInsertNodeOnEdge } = nodeActions;

  const { centerOnSelected } = useFlowchartKeyboard({
    selectedNodes,
    isFocusMode,
    centerOnTask,
    focusSelectedTasks,
    handleExitFocusMode,
  });

  const { copyNode, duplicateNode, pasteAtPosition, pasteConnectedToHost, pasteBeforeHost, hasClipboardData } = useClipboard({
    tasks,
    selectedNodes,
    setSelectedNodes,
    nodePositions,
    nestedSprints,
    allTags,
    customFields,
    onPasteTasks,
    onDeleteTasks,
    onAfterDelete: handleAfterDeleteStable,
    isMultiCampaignMode,
    openCampaignPickerForPaste,
    loadFullSprint,
    allSprints,
    findEmptySpaceNearNode: findEmptySpaceNearNodeFromUtils,
  });

  const canvasContextMenu = useCanvasContextMenu({
    setTaskPosition,
    onAddTask,
    pasteAtPosition,
    setSelectedNodes,
  });

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('flowchartSelectionChange', {
        detail: { hasSelection: selectedNodes.size > 0 },
      }),
    );
  }, [selectedNodes]);

  useEffect(() => {
    const handleAutoPan = (event) => {
      const { targetPan } = event.detail;
      console.log('[FlowchartCore] Received flowchartAutoPan event, panning to:', targetPan);
      if (setPanAnimated && targetPan) {
        setPanAnimated(targetPan);
      }
    };

    window.addEventListener('flowchartAutoPan', handleAutoPan);
    return () => window.removeEventListener('flowchartAutoPan', handleAutoPan);
  }, [setPanAnimated]);

  const campaignDrop = useCampaignDrop({
    containerRef,
    pan,
    zoom,
    campaignId,
    onNestCampaign,
    isMultiCampaignMode,
    onAddCampaignToView,
    currentCampaignIds: multiCampaignCampaigns.map((c) => c.id),
  });

  const closeCanvasContextMenu = useCallback(() => {
    canvasMenuRef?.current?.dismiss();
    setCanvasContextMenuState(null);
  }, [canvasMenuRef, setCanvasContextMenuState]);

  return {
    virtualizedTasksToRender,
    fullNodeIds,
    virtualizedEdgesToRender,
    virtualizationStats: virtualization.stats,
    getViewportCenter,
    findEmptySpaceInViewport: findEmptySpaceInViewportFromUtils,
    findPositionInCampaign,
    isViewReady,
    setTaskPosition,
    handleCreateConnectedNode,
    handleInsertNodeOnEdge,
    centerOnSelected,
    copyNode,
    duplicateNode,
    pasteConnectedToHost,
    pasteBeforeHost,
    hasClipboardData,
    canvasContextMenu,
    hoveredNodeId,
    campaignDrop,
    closeCanvasContextMenu,
  };
}
